import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CourseCard from './components/CourseCard';
import SearchDropdown, { SuggestionItem } from '../../components/base/SearchDropdown';
import type { Course } from '../../mocks/courses';
import { api } from '@/lib/api';
import { adaptCourseListItem, buildCategoryOptions, createCategoryMetaResolver, createCategoryNameResolver } from '@/lib/adapters';
import { useAuth } from '@/lib/auth';

const SORTS = [
  { label: '評分最高', value: 'rating' },
  { label: '最多報名', value: 'students' },
  { label: '價格從低', value: 'price-asc' },
  { label: '價格從高', value: 'price-desc' }
];

const FIXED_LEVELS = ['入門', '簡單', '適中', '困難'];
const FIXED_LANGUAGES = ['中文', 'English'];

const CATEGORY_STYLES = [
  { icon: 'ri-cpu-line', gradient: 'from-violet-500 to-indigo-600' },
  { icon: 'ri-code-s-slash-line', gradient: 'from-sky-500 to-cyan-600' },
  { icon: 'ri-bar-chart-grouped-line', gradient: 'from-teal-500 to-emerald-600' },
  { icon: 'ri-product-hunt-line', gradient: 'from-orange-500 to-rose-500' },
  { icon: 'ri-briefcase-4-line', gradient: 'from-amber-500 to-orange-600' },
  { icon: 'ri-rocket-line', gradient: 'from-fuchsia-500 to-violet-600' }
];

const AI_BG_KEYWORDS = new Set([
  'ai-neural-grid',
  'ai-cyber-flow',
  'ai-quantum-core',
  'ai-data-wave',
  'ai-fusion-net',
  'ai-deep-space'
]);

const idToNum = (value: string | number) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const idToKey = (value: string | number) => String(value);
const isImageBg = (value?: string) => Boolean(value && (/^https?:\/\//.test(value) || value.startsWith('/') || value.startsWith('data:image')));
const categoryStyleOf = (id: string | number) => CATEGORY_STYLES[Math.abs(idToNum(id)) % CATEGORY_STYLES.length];
const categoryIconOf = (category: { id: string | number; icon?: string }) => category.icon || categoryStyleOf(category.id).icon;
const categoryBgStyleOf = (category: { id: string | number; icon?: string; bg?: string }) => {
  if (isImageBg(category.bg)) {
    return { backgroundImage: `url("${category.bg}")` };
  }
  const style = categoryStyleOf(category.id);
  const presetEnabled = Boolean(category.bg && AI_BG_KEYWORDS.has(category.bg));
  return {
    backgroundImage: presetEnabled
      ? `linear-gradient(145deg, rgba(10,22,40,0.94), rgba(37,99,235,0.88), rgba(124,58,237,0.9))`
      : `linear-gradient(145deg, #0f172a 0%, #1e293b 55%, #334155 100%)`,
    boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.06)`
  };
};

const CoursesPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const initialQuery = searchParams.get('q') || '';
  const [search, setSearch] = useState(initialQuery);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [categories, setCategories] = useState<Array<{ id: string | number; name: string; icon?: string; bg?: string }>>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [gridKey, setGridKey] = useState(0);
  const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const scrollToResults = useCallback(() => {
    window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const plans = await api.getMembershipPlans();
        const monthlyPlan = plans
          .filter((plan) => (plan.planPrice ?? 0) > 0)
          .sort((a, b) => Number(a.planPrice ?? 0) - Number(b.planPrice ?? 0))
          .find((plan) => {
            const billing = String(plan.billingCycle || '').toLowerCase();
            if (billing.includes('month') || billing.includes('月')) return true;
            if (plan.durationDays && plan.durationDays <= 31) return true;
            return false;
          });
        if (monthlyPlan) {
          setMonthlyPrice(Number(monthlyPlan.planPrice ?? 0));
        }
      } catch {
        setMonthlyPrice(null);
      }
      const categoryTree = await api.getCategories();
      const categoryNameOf = createCategoryNameResolver(categoryTree);
      const categoryMetaOf = createCategoryMetaResolver(categoryTree);
      setCategories(buildCategoryOptions(categoryTree));
      const page = await api.searchCourses({
        pageCurrent: 1,
        pageSize: 100,
        courseName: initialQuery || undefined
      });
      const baseCourses = page.list.map((item) => adaptCourseListItem(item, categoryNameOf(item.categoryId), categoryMetaOf(item.categoryId)));
      if (!isAuthenticated) {
        setCourses(baseCourses);
        setLoading(false);
        return;
      }
      try {
        const myCourses = await api.getMyCourses({ pageCurrent: 1, pageSize: 500 });
        const ownedIds = new Set(
          (myCourses.list || []).map((item) => String(item.courseId || item.courseResp?.id || ''))
        );
        setCourses(
          baseCourses.map((course) => ({
            ...course,
            isPurchased: ownedIds.has(String(course.id))
          }))
        );
      } catch {
        setCourses(baseCourses);
      }
      setLoading(false);
    })();
  }, [initialQuery, isAuthenticated]);

  useEffect(() => {
    if (initialQuery) {
      scrollToResults();
    }
  }, [initialQuery, scrollToResults]);

  useEffect(() => {
    setGridKey((value) => value + 1);
  }, [search, selectedCategory, selectedLevel, selectedLanguage, sortBy]);

  const filtered = useMemo(() => {
    let list = [...courses];
    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter(
        (course) =>
          course.title.toLowerCase().includes(query) ||
          course.instructor.toLowerCase().includes(query) ||
          course.topics.some((topic) => topic.toLowerCase().includes(query))
      );
    }
    if (selectedCategory) {
      list = list.filter((course) => idToKey(course.categoryId || '') === selectedCategory);
    }
    if (selectedLevel) {
      list = list.filter((course) => String(course.level || '') === selectedLevel);
    }
    if (selectedLanguage) {
      list = list.filter((course) => String(course.language || '').includes(selectedLanguage));
    }
    list.sort((left, right) => {
      if (sortBy === 'students') return right.students - left.students;
      if (sortBy === 'price-asc') return left.price - right.price;
      if (sortBy === 'price-desc') return right.price - left.price;
      return right.rating - left.rating;
    });
    return list;
  }, [categories, courses, search, selectedCategory, selectedLevel, selectedLanguage, sortBy]);

  const levelOptions = FIXED_LEVELS;
  const languageOptions = FIXED_LANGUAGES;

  const suggestions = useMemo((): SuggestionItem[] => {
    if (!search.trim()) return [];
    return filtered.slice(0, 8).map((course) => ({
      label: course.title,
      sub: `${course.instructor} · ${course.category}`,
      type: 'course',
      icon: 'ri-book-open-line',
      value: course.title
    }));
  }, [filtered, search]);

  const categoryCounts = useMemo(() => {
    const base = search.trim()
      ? courses.filter(
          (course) =>
            course.title.toLowerCase().includes(search.toLowerCase()) ||
            course.instructor.toLowerCase().includes(search.toLowerCase()) ||
            course.topics.some((topic) => topic.toLowerCase().includes(search.toLowerCase()))
        )
      : courses;

    return Object.fromEntries(
      categories.map((category) => [
        idToKey(category.id),
        base.filter((course) => idToKey(course.categoryId || '') === idToKey(category.id)).length
      ])
    );
  }, [categories, courses, search]);

  const selectedCategoryName = categories.find((item) => idToKey(item.id) === selectedCategory)?.name;
  const totalStudents = useMemo(
    () => courses.reduce((sum, course) => sum + Number(course.students || 0), 0),
    [courses]
  );
  const hotKeywords = useMemo(
    () =>
      Array.from(
        new Set(
          courses
            .flatMap((course) => [course.category, ...course.topics])
            .filter(Boolean)
        )
      ).slice(0, 5),
    [courses]
  );
  const activeChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; onRemove: () => void }> = [];
    if (search.trim()) {
      chips.push({ id: 'search', label: `"${search}"`, onRemove: () => setSearch('') });
    }
    if (selectedCategoryName) {
      chips.push({ id: 'category', label: selectedCategoryName, onRemove: () => setSelectedCategory(null) });
    }
    if (selectedLevel) {
      chips.push({ id: 'level', label: `等级：${selectedLevel}`, onRemove: () => setSelectedLevel('') });
    }
    if (selectedLanguage) {
      chips.push({ id: 'language', label: `语言：${selectedLanguage}`, onRemove: () => setSelectedLanguage('') });
    }
    return chips;
  }, [search, selectedCategoryName, selectedLevel, selectedLanguage]);

  return (
    <div className="min-h-screen bg-[#F8F9FC] font-sans">
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <div className="bg-[#0A1628] py-10 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <span className="mb-3 block text-xs font-semibold uppercase tracking-[0.32em] text-violet-400">
                  NexusGo 課程中心
                </span>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight leading-tight">
                  由頂尖外商導師主講
                  <br />
                  系統化 AI 職涯課程
                </h1>
                <p className="text-slate-400 text-sm leading-relaxed mb-5 sm:mb-6 max-w-xl">
                  每門課程由來自全球頂尖外商公司的在職專家親授，
                  結合真實業界經驗，助你以最短路徑拿下理想 Offer。
                </p>
                <div className="flex items-center gap-5 sm:gap-6">
                  {[
                    { value: `${courses.length}`, label: '門精品課程' },
                    { value: `${totalStudents.toLocaleString()}+`, label: '名學員報名' }
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="text-xl font-bold text-white">{item.value}</div>
                      <div className="text-slate-400 text-xs mt-0.5">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full max-w-xl">
                <div className="rounded-[28px] border border-white/10 bg-white/5 p-3 backdrop-blur-sm">
                  <SearchDropdown
                    search={search}
                    onSearchChange={(value) => {
                      setSearch(value);
                      if (value.trim()) {
                        scrollToResults();
                      }
                    }}
                    onClear={() => setSearch('')}
                    suggestions={suggestions}
                    onSuggestionClick={(item) => {
                      setSearch(item.value);
                      scrollToResults();
                    }}
                    placeholder="搜尋課程、講師、技能..."
                    showDropdown={showDropdown}
                    onShowDropdownChange={setShowDropdown}
                    inputClassName="bg-white/10 text-white placeholder-slate-400 border-white/10 focus:border-violet-400"
                  />
                </div>
                {!search.trim() && hotKeywords.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-slate-500 text-[11px] whitespace-nowrap">熱門：</span>
                    {(hotKeywords.length >= 5 ? hotKeywords : ['大型語言模型', 'FAANG 面試', 'ML 工程', '資料科學', 'AI PM']).slice(0, 5).map((keyword) => (
                      <button
                        key={keyword}
                        onClick={() => {
                          setSearch(keyword);
                          scrollToResults();
                        }}
                        className="px-2 py-0.5 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white text-[11px] rounded-full cursor-pointer transition-all border border-white/10 whitespace-nowrap"
                      >
                        {keyword}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3 bg-gradient-to-r from-violet-600/80 to-indigo-600/80 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center justify-between border border-violet-400/30">
                  <div>
                    <p className="text-white font-bold text-sm">會員訂閱</p>
                    <p className="text-violet-200 text-xs mt-0.5">全部課程無限學習 · 每月更新</p>
                  </div>
                  <button
                    onClick={() => navigate('/services')}
                    className="px-3 py-1.5 bg-white text-violet-700 text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap hover:bg-violet-50 transition-colors"
                  >
                    {monthlyPrice ? `$${monthlyPrice}/mo` : '查看方案'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          {categories.length > 0 && (
            <div className="mb-8">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                {categories.map((category) => {
                  const style = categoryStyleOf(category.id);
                  const categoryIcon = categoryIconOf(category);
                  const categoryKey = idToKey(category.id);
                  const isActive = selectedCategory === categoryKey;
                  const count = categoryCounts[categoryKey] || 0;
                  const cardBgStyle = categoryBgStyleOf(category);
                  return (
                    <button
                      key={categoryKey}
                      onClick={() => {
                        setSelectedCategory((current) => (current === categoryKey ? null : categoryKey));
                        scrollToResults();
                      }}
                      className={`group relative overflow-hidden rounded-[24px] p-4 text-left transition-all duration-200 bg-cover bg-center ${
                        isActive
                          ? 'shadow-[0_20px_40px_-24px_rgba(10,22,40,0.85)] ring-1 ring-[#0A1628]'
                          : 'border border-slate-200 hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-[0_16px_36px_-28px_rgba(76,29,149,0.35)]'
                      }`}
                      style={cardBgStyle}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/60" />
                      <div className={`pointer-events-none absolute -right-2 -top-1 opacity-20 text-white`}>
                        <i className={`${categoryIcon} text-6xl`} />
                      </div>
                      <div className={`relative mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${style.gradient}`}>
                        <i className={`${categoryIcon} text-lg text-white`} />
                      </div>
                      <div className={`relative text-sm font-bold leading-snug text-white`}>
                        {category.name}
                      </div>
                      <div className="relative mt-2 text-[11px] font-medium text-white/70">
                        {count} 門課程
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-6 rounded-[28px] border border-slate-200 bg-white px-5 py-4 shadow-[0_12px_36px_-28px_rgba(15,23,42,0.35)] sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500 flex items-center gap-2">
                {(search.trim() || selectedCategoryName) ? (
                  <>
                    篩選結果：
                    <span className="font-bold text-[#0A1628] text-base tabular-nums">{filtered.length}</span>
                    <span>/ {courses.length} 門課程</span>
                  </>
                ) : (
                  <>
                    共{' '}
                    <span className="font-bold text-[#0A1628] text-base tabular-nums">{filtered.length}</span>{' '}
                    門課程
                  </>
                )}
              </p>

              <div className="flex items-center gap-2 self-start sm:self-auto">
                <select
                  value={selectedLevel}
                  onChange={(event) => setSelectedLevel(event.target.value)}
                  className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 border-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="">全部等级</option>
                  {levelOptions.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
                <select
                  value={selectedLanguage}
                  onChange={(event) => setSelectedLanguage(event.target.value)}
                  className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 border-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="">全部语言</option>
                  {languageOptions.map((language) => (
                    <option key={language} value={language}>{language}</option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 border-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  {SORTS.map((sort) => (
                    <option key={sort.value} value={sort.value}>{sort.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {activeChips.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <span className="text-xs text-slate-400">已篩選：</span>
                {activeChips.map((chip) => (
                  <span
                    key={chip.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700"
                  >
                    {chip.label}
                    <button
                      onClick={chip.onRemove}
                      className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-violet-200/80"
                    >
                      <i className="ri-close-line text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 pb-12">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_12px_36px_-28px_rgba(15,23,42,0.35)]"
                >
                  <div className="aspect-[16/10] animate-pulse bg-slate-100" />
                  <div className="space-y-3 p-5">
                    <div className="h-4 w-20 animate-pulse rounded-full bg-slate-100" />
                    <div className="h-6 w-4/5 animate-pulse rounded-xl bg-slate-100" />
                    <div className="h-4 w-full animate-pulse rounded-xl bg-slate-100" />
                    <div className="h-4 w-3/4 animate-pulse rounded-xl bg-slate-100" />
                    <div className="flex gap-2 pt-2">
                      <div className="h-8 w-20 animate-pulse rounded-full bg-slate-100" />
                      <div className="h-8 w-16 animate-pulse rounded-full bg-slate-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-slate-200 bg-white py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <i className="ri-search-eye-line text-2xl" />
              </div>
              <div className="text-sm font-medium text-slate-500">沒有找到符合條件的課程</div>
              <div className="mt-1 text-xs text-slate-400">試試調整篩選條件，或直接搜尋關鍵字</div>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {['AI 產品', 'ML 系統', 'Python', '面試衝刺', '資料科學'].map((keyword) => (
                  <button
                    key={keyword}
                    onClick={() => setSearch(keyword)}
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs rounded-full cursor-pointer hover:border-violet-300 hover:text-violet-700 transition-colors whitespace-nowrap"
                  >
                    {keyword}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setSearch('');
                  setSelectedCategory(null);
                  setSelectedLevel('');
                  setSelectedLanguage('');
                }}
                className="mt-5 px-4 py-2 bg-[#0A1628] text-white text-xs font-semibold rounded-lg cursor-pointer hover:bg-slate-800 transition-colors whitespace-nowrap"
              >
                清除所有篩選
              </button>
            </div>
          ) : (
            <div ref={resultsRef} key={gridKey} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 pb-12">
              {filtered.map((course, index) => (
                <div
                  key={course.id}
                  className="opacity-0 translate-y-3"
                  style={{
                    animation: 'fadeSlideIn 0.35s ease forwards',
                    animationDelay: `${index * 45}ms`
                  }}
                >
                  <CourseCard course={course} highlight={search} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CoursesPage;
